/**
 * Types partagés pour les circuits de rallycross
 */

/**
 * Types de cases du circuit
 */
export enum TrackCellType {
  EMPTY = 0,
  STRAIGHT = 1, // Ligne droite
  TURN = 2, // Virage
  START = 3, // Départ (pour plus tard)
  /** @deprecated Réservé — ancienne case bonus retirée ; normalisée en STRAIGHT au chargement. */
  JOKER = 4,
  JUMP = 5, // Saut (pour plus tard)
  JOKER_LAP = 6, // Tour joker : passage alternatif (une fois par pilote)
  START_LANE = 7, // Case de la ligne de départ (extension hors circuit)
}

/** Nombre fixe de cases pour une ligne de départ rallycross. */
export const START_LANE_LENGTH = 5;

/** Ligne de départ : tronçon droit qui rejoint le circuit principal. */
export interface StartLane {
  /** Index sur le path principal (point de jonction avec le circuit). */
  joinPathIndex: number;
  /** Cases de la ligne (index 0 = proche du circuit, index 4 = fond de grille). */
  cells: TrackCell[];
}

/** Branche « tour joker » — passage alternatif obligatoire une fois par course. */
export interface JokerLapBranch {
  /** Index sur le path principal (divergence). */
  entryPathIndex: number;
  /** Index sur le path principal (rejoint le circuit). */
  exitPathIndex: number;
  /** Cases du passage alternatif (type JOKER_LAP). */
  cells: TrackCell[];
}

/**
 * Représente une case du circuit
 */
export interface TrackCell {
  x: number;
  y: number;
  type: TrackCellType;
}

/**
 * Direction de déplacement sur la grille
 */
export enum Direction {
  NORTH = 0, // Haut
  EAST = 1, // Droite
  SOUTH = 2, // Bas
  WEST = 3, // Gauche
}

/** Surface décorative d’une case (éditeur). */
export type CircuitSurface = 'asphalt' | 'earth' | 'damier';

/** Bord d’une case pour le décor. */
export type CircuitEdge = 'north' | 'east' | 'south' | 'west';

/** Couleur d’un bord de case (éditeur). */
export type CircuitEdgeColor = 'white' | 'red' | 'curb';

/** Style visuel optionnel par coordonnée grille. */
export interface CircuitCellStyle {
  fill?: CircuitSurface;
  edges?: Partial<Record<CircuitEdge, CircuitEdgeColor>>;
}

/**
 * Circuit généré
 */
export interface Circuit {
  grid: TrackCellType[][]; // Grille 2D pour affichage
  path: TrackCell[]; // Chemin du circuit
  width: number;
  height: number;
  /** Passage alternatif optionnel (tour joker). */
  jokerLap?: JokerLapBranch;
  /** Ligne de départ optionnelle (5 cases, définie dans l’éditeur). */
  startLane?: StartLane;
  /** Décor bitume / terre par case (clé « x,y »). */
  cellStyles?: Record<string, CircuitCellStyle>;
  /** Cases décoratives en damier autour du circuit (hors tracé). */
  decorCells?: Array<{ x: number; y: number }>;
  /** Gradins avec spectateurs (plusieurs cases). */
  grandstands?: CircuitGrandstand[];
  /** Panneaux personnalisables. */
  signs?: CircuitSign[];
  /** Véhicules décoratifs (image 25×25 px par case). */
  vehicles?: CircuitVehicle[];
  /** Regroupements cosmétiques de cases du tracé sous un modèle 3D multi-case. */
  roadPieces?: CircuitRoadPiece[];
  /** Décor 3D Kenney hors tracé (tentes, tribunes, panneaux, feux, caméras, etc.). */
  decor3d?: CircuitDecor3d[];
  /** Décor en placement libre (éditeur 3D) : position/rotation continues, superposition autorisée. */
  freeDecor?: CircuitFreeDecor[];
}

/** Bloc de gradins sur la grille. */
export interface CircuitGrandstand {
  id: string;
  cells: Array<{ x: number; y: number }>;
}

/** Panneau affichant un texte libre. */
export interface CircuitSign {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
}

/** Véhicule décoratif sur une case (sprite catalogue). */
export interface CircuitVehicle {
  id: string;
  x: number;
  y: number;
  spriteId: string;
}

/** Référence à un modèle du catalogue 3D circuit (Kenney Racing Kit) + orientation grille. */
export interface Catalog3dRef {
  modelId: string;
  /** Orientation du modèle, alignée sur la grille (pas de 90°). */
  rotation: Direction;
}

/**
 * Regroupement cosmétique de cases déjà existantes du tracé (path / tour
 * joker / ligne de départ) sous UN modèle 3D multi-case (ex. "roadStraightLarge"
 * sur 2 cases au lieu de 2 modèles 1-case). Ne modifie ni ne duplique
 * `path`/`jokerLap`/`startLane` — pure couche de rendu.
 */
export interface CircuitRoadPiece extends Catalog3dRef {
  id: string;
  source: 'path' | 'jokerLap' | 'startLane';
  /**
   * `shape: 'straight'` (défaut) : index de la première case d'une run rectiligne
   * de `cellCost` cases (1 à 3), qui s'étend vers l'avant.
   * `shape: 'corner'` : index de la case du virage lui-même — la pièce s'étend
   * symétriquement sur les deux bras adjacents (`cellCost` 1, 3 ou 5 : la case de
   * virage plus `(cellCost-1)/2` cases droites de chaque côté), un virage "Large"/
   * "Larger" balayant une zone réelle de 2×2/3×3 cases, pas une simple run droite.
   */
  startIndex: number;
  /** Nombre de cases couvertes — doit égaler la run réellement occupée (voir `shape`). */
  cellCost: number;
  /** Absent = 'straight' (compatibilité avec les pièces existantes). */
  shape?: 'straight' | 'corner';
}

/** Sous-catégories de décor 3D Kenney hors tracé. */
export type CircuitDecor3dKind =
  | 'grandstand'
  | 'vehicleStatic'
  | 'tent'
  | 'billboard'
  | 'light'
  | 'camera'
  | 'pitBuilding'
  | 'fence'
  | 'tree'
  | 'prop';

/**
 * Décor 3D Kenney hors tracé, à empreinte multi-case arbitraire (généralise
 * `CircuitGrandstand` en ajoutant l'identité catalogue + la rotation).
 */
export interface CircuitDecor3d extends Catalog3dRef {
  id: string;
  kind: CircuitDecor3dKind;
  cells: Array<{ x: number; y: number }>;
  /** Texte libre optionnel (kind: 'billboard'). */
  text?: string;
}

/**
 * Décor en placement libre (éditeur 3D) : position continue (pas alignée sur la
 * grille) et rotation libre, sans empreinte de cases — contrairement à
 * `CircuitDecor3d`, plusieurs entrées peuvent se superposer librement (voir
 * `shared/game/circuitFreeDecorEdit.ts`, sans vérification d'occupation).
 */
export interface CircuitFreeDecor {
  id: string;
  kind: CircuitDecor3dKind;
  modelId: string;
  /** Position libre en unités de case (peut être décimale). */
  x: number;
  y: number;
  /** Rotation libre en degrés (0-359), pas seulement des paliers de 90°. */
  rotationDeg: number;
  /** Décalage vertical (axe Y, mêmes unités que `TILE_SIZE`) au-dessus du sol — absent ou 0 = posé au sol. */
  elevation?: number;
  /** Texte libre optionnel (kind: 'billboard') — parité avec `CircuitDecor3d`, non câblé en 3D pour l'instant. */
  text?: string;
}

/** Position d’un pilote sur le tracé (principal ou branche joker). */
export interface PlayerTrackPosition {
  mainPathIndex: number;
  /** Index dans jokerLap.cells, ou null si sur le path principal. */
  jokerBranchIndex: number | null;
  /** Index dans startLane.cells, ou null si hors ligne de départ. */
  startLaneIndex: number | null;
}

/**
 * Paramètres de génération d'un circuit
 */
export interface CircuitGenerationParams {
  numStraights: number; // Nombre de lignes droites
  numTurns: number; // Nombre de virages
  approximateCells: number; // Nombre approximatif de cases
}

/**
 * Résultat de validation d'un circuit
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}
